/*
 * The auth routes of protocol.md §3.2, typed once. Every screen in this area calls through here and
 * none of them writes a path or a body shape of its own.
 *
 * The bodies are hand-written rather than generated: `internal/core/auth` answers with its own view
 * structs (`SessionView`, `DeviceView`, `SecretPreview`, the login outcome), and tygo generates only
 * the two `wire` packages (tygo.yaml) — so this file is the mirror, and its field names come from
 * the route table verbatim.
 *
 * Nothing here stores anything. The session is a `__Host-` cookie the browser keeps, the CSRF token
 * lives in memory in `core/net/api.ts`, and a password, a code or a link secret exists only for the
 * duration of the call that carries it (spec 01 §5).
 */

import { draftMirror } from "../drafts/index.ts"
import { api, setCsrfToken } from "../net/api.ts"
import type { SelfUser, StepUp } from "../wire/core.gen.ts"
import { normaliseCode } from "./code.ts"
import { markSignedOut } from "./signedOut.ts"

/** The session half of `GET /api/auth/session`. */
export interface SessionInfo {
	id: number
	stepUp: StepUp
	trustedDevice: boolean
}

export interface SessionResponse {
	user: SelfUser | null
	session: SessionInfo | null
	csrfToken: string
}

/** The outcomes a login, a code, an enrolment and a reset share (protocol.md §3.2). */
export type LoginOutcome = "session" | "codeRequired" | "invalid" | "deactivated" | "burned"

export interface LoginResponse {
	outcome: LoginOutcome
	/** How long the waiting tab has, on `codeRequired`. */
	pendingTtlMs?: number
	/** How many of the five attempts are left, on `invalid` for a code. */
	attemptsLeft?: number
}

/** The two kinds of link a person can be sent (`auth.secret.inspect`). */
export type SecretKind = "invitation" | "passwordReset"

/**
 * What a link is worth, read without spending it. Every failure — unknown, expired, spent, or an
 * account that was deactivated meanwhile — is the identical `{valid:false}`, so this shape never
 * answers a question the caller did not already know the answer to.
 */
export interface SecretPreview {
	valid: boolean
	kind?: SecretKind
	email?: string
	displayName?: string
	expiresAt?: number
}

/**
 * `PATCH /api/users/me`: every field optional, an absent field leaves the column. The avatar is an
 * attachment id the server resolves inside its own write — it must be one the caller uploaded with
 * the scope `avatar`, and anything else is answered as "this attachment does not exist"
 * (security.md §9.5) — and 0 clears the picture.
 *
 * The four status fields are four independent fields and not one object, which is what lets the
 * account menu tick *Show me as away* without touching the line somebody wrote: `statusAway` alone
 * leaves the emoji, the text and the end time as they are. Clearing a status is the other three
 * together (`statusEmoji: ""`, `statusText: ""`, `statusUntil: 0`), and it leaves the away claim —
 * taking a lunch line down is not a statement that somebody is back. The route decodes with unknown
 * fields refused, so these names are exactly the names the server reads (protocol.md §3.2).
 */
export interface ProfilePatch {
	displayName?: string
	fullName?: string
	title?: string
	pronouns?: string
	handle?: string
	locale?: string
	timezone?: string
	avatarAttachmentId?: number
	/** One emoji of `wire.StatusEmojiPalette`, or `""` for none. */
	statusEmoji?: string
	statusText?: string
	/** Epoch-ms UTC when the status clears itself; 0 is "never", and a past moment is refused. */
	statusUntil?: number
	statusAway?: boolean
}

/*
 * A browser, as the two lists of `/settings/security` receive it. `userAgent` is the raw string the
 * browser sent about itself and `deviceName.ts` turns it into the name a reader sees; `label` is the
 * server's own reading of the same agent, which the login-alert mail needs and which names a row
 * stored before the field crossed the wire. The field is absent, not empty, where there is nothing
 * to send (`omitempty`).
 */
export interface SessionRow {
	id: number
	label: string
	userAgent?: string
	createdAt: number
	lastSeenAt: number
	ipPrefix: string
	current: boolean
	deviceId?: number
}

export interface DeviceRow {
	id: number
	label: string
	userAgent?: string
	createdAt: number
	lastSeenAt: number
	trustedUntil: number
	current: boolean
}

export const authApi = {
	/**
	 * The first request of every tab. It mints the pre-session double-submit cookie for an anonymous
	 * caller and re-derives the session's synchroniser token for a signed-in one, so all tabs of one
	 * session hold the same value (security.md §5.2).
	 */
	async session(): Promise<SessionResponse> {
		const res = await api.get<SessionResponse>("/api/auth/session")
		// Signed in, the token is the session's and is held in memory only. Anonymous, the token is
		// the cookie the answer just set, and `core/net/api.ts` reads it from there — which it only
		// does while the in-memory value is empty.
		setCsrfToken(res.user ? res.csrfToken : "")
		return res
	},

	login: (identifier: string, password: string) =>
		api.post<LoginResponse>("/api/auth/login", { identifier, password }),

	verifyLoginCode: (code: string) =>
		api.post<LoginResponse>("/api/auth/login/code", { code: normaliseCode(code) }),

	resendLoginCode: () => api.post<void>("/api/auth/login/code/resend"),

	/**
	 * Ends this session. The screen that follows is the sign-in form, and it should say why it is
	 * there rather than look like a session that fell over — so the fact is left for it here, at the
	 * one route every way out of the product goes through (`signedOut.ts`).
	 */
	logout: async () => {
		// A half-written message goes to the server mirror first, while this session still exists: after
		// the call below the draft could only be written from this device again (core/drafts/mirror.ts).
		// The flush carries its own deadline, so a request that hangs holds this gesture up for a moment
		// and never for a browser timeout — and nothing is lost when it expires, because the text is on
		// this device's disk and the `pagehide` beacon offers it again.
		await draftMirror()?.flush()
		await api.post<void>("/api/auth/logout")
		markSignedOut()
	},

	inspectSecret: (kind: SecretKind, k: string) =>
		api.post<SecretPreview>("/api/auth/secret/inspect", { kind, k }),

	enrol: (k: string, password: string) =>
		api.post<LoginResponse>("/api/auth/enrolment", { k, password }),

	requestPasswordReset: (email: string) =>
		api.post<void>("/api/auth/password/reset/request", { email }),

	redeemPasswordReset: (k: string, password: string) =>
		api.post<LoginResponse>("/api/auth/password/reset", { k, password }),

	changePassword: (current: string, next: string) =>
		api.post<void>("/api/auth/password", { current, next }),

	stepUpPassword: (password: string) =>
		api.post<{ passwordStepupAt: number }>("/api/auth/stepup/password", { password }),

	requestStepUpCode: () => api.post<void>("/api/auth/stepup/code/request"),

	verifyStepUpCode: (code: string) =>
		api.post<{ codeStepupAt: number }>("/api/auth/stepup/code/verify", {
			code: normaliseCode(code),
		}),

	/**
	 * The own profile. The answer is the fresh `SelfUser`; every other browser learns of the change
	 * through `account.updated`, which is what keeps a second tab and everybody else's sidebar right.
	 */
	updateProfile: (patch: ProfilePatch) =>
		api.patch<{ user: SelfUser }>("/api/users/me", patch).then((body) => body.user),

	listSessions: () => api.get<{ sessions: SessionRow[] }>("/api/auth/sessions"),

	revokeSession: (id: number) => api.del<void>(`/api/auth/sessions/${id}`),

	revokeOtherSessions: () => api.post<{ revoked: number }>("/api/auth/sessions/revoke-others"),

	listDevices: () => api.get<{ devices: DeviceRow[] }>("/api/auth/devices"),

	revokeDevice: (id: number) => api.del<void>(`/api/auth/devices/${id}`),
}
